<template>
  <BackofficeLayout>
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-2xs">
        <div>
          <h1 class="text-xl font-bold text-slate-900">Manajemen Master Produk</h1>
          <p class="text-xs text-slate-500 mt-1">Kelola master produk, operator seluler, tipe transaksi, mode nominal, dan skema input pelanggan PPOB.</p>
        </div>
        <button
          @click="openCreateModal"
          type="button"
          class="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-xs transition flex items-center justify-center gap-2"
        >
          <Plus class="w-4 h-4" />
          <span>Tambah Master Produk</span>
        </button>
      </div>

      <!-- Flash Status -->
      <div v-if="$page.props.flash.status" class="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-semibold rounded-xl">
        {{ $page.props.flash.status }}
      </div>

      <!-- Filters & Table Section -->
      <div class="bg-white rounded-2xl border border-slate-200 shadow-2xs overflow-hidden">
        <div class="p-4 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-4">
          <div class="flex items-center gap-3">
            <select v-model="selectedCategory" @change="applyFilters" class="px-3.5 py-2 bg-white border border-slate-300 text-slate-900 rounded-xl text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
              <option :value="null">Semua Kategori</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
            </select>
            <input
              v-model="search"
              type="text"
              placeholder="Cari produk (nama, kode, brand)..."
              class="px-3.5 py-2 bg-white border border-slate-300 text-slate-900 rounded-xl text-xs w-64 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              @input="applyFilters"
            />
          </div>
          <span class="text-xs font-semibold text-slate-500">Total: {{ products.length }} Produk</span>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full text-left border-collapse text-xs">
            <thead>
              <tr class="border-b border-slate-200 bg-slate-100/70 text-slate-700 font-bold uppercase tracking-wider text-[10px]">
                <th class="py-3 px-4">Urutan</th>
                <th class="py-3 px-4">Kategori</th>
                <th class="py-3 px-4">Operator Master</th>
                <th class="py-3 px-4">Kode Produk</th>
                <th class="py-3 px-4">Nama Produk & Brand</th>
                <th class="py-3 px-4">Tipe Transaksi</th>
                <th class="py-3 px-4">Mode Nominal</th>
                <th class="py-3 px-4">SKU</th>
                <th class="py-3 px-4">Status</th>
                <th class="py-3 px-4 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 text-slate-800">
              <tr v-for="p in products" :key="p.id" class="hover:bg-slate-50/80 transition">
                <td class="py-3 px-4 font-mono font-bold text-slate-500">{{ p.sort_order }}</td>
                <td class="py-3 px-4 font-semibold text-slate-700">{{ p.category?.name || '-' }}</td>
                <td class="py-3 px-4">
                  <span v-if="p.operator" class="px-2 py-0.5 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-100 text-[10px] font-bold">
                    {{ p.operator.name }}
                  </span>
                  <span v-else class="text-slate-400 italic text-[11px]">-</span>
                </td>
                <td class="py-3 px-4 font-mono font-bold text-indigo-600">{{ p.code }}</td>
                <td class="py-3 px-4">
                  <p class="font-bold text-slate-900">{{ p.name }}</p>
                  <span v-if="p.brand" class="text-[10px] text-slate-500 font-medium">Brand: {{ p.brand }}</span>
                </td>
                <td class="py-3 px-4">
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-slate-100 text-slate-700 border border-slate-200">
                    {{ p.transaction_type }}
                  </span>
                </td>
                <td class="py-3 px-4">
                  <span class="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-50 text-amber-800 border border-amber-200 uppercase">
                    {{ p.amount_mode || 'predefined' }}
                  </span>
                </td>
                <td class="py-3 px-4 font-bold text-slate-700">{{ p.skus_count }} SKU</td>
                <td class="py-3 px-4">
                  <span
                    :class="p.is_active ? 'bg-emerald-100 text-emerald-800 border-emerald-200' : 'bg-slate-100 text-slate-600 border-slate-200'"
                    class="px-2.5 py-1 rounded-full border text-[10px] font-bold"
                  >
                    {{ p.is_active ? 'Aktif' : 'Nonaktif' }}
                  </span>
                </td>
                <td class="py-3 px-4 text-right space-x-2">
                  <button
                    @click="toggleActive(p)"
                    type="button"
                    class="px-2.5 py-1 rounded-lg border text-[11px] font-semibold transition"
                    :class="p.is_active ? 'border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100' : 'border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'"
                  >
                    {{ p.is_active ? 'Nonaktifkan' : 'Aktifkan' }}
                  </button>
                  <button @click="openEditModal(p)" type="button" class="px-2.5 py-1 rounded-lg border border-slate-200 bg-slate-100 text-slate-700 hover:bg-slate-200 text-[11px] font-semibold transition">
                    Edit
                  </button>
                  <button @click="deleteProduct(p)" type="button" class="px-2.5 py-1 rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 text-[11px] font-semibold transition">
                    Hapus
                  </button>
                </td>
              </tr>
              <tr v-if="products.length === 0">
                <td colspan="10" class="py-8 text-center text-slate-500 text-xs">
                  Tidak ada data master produk.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Modal Create / Edit Product -->
      <div v-if="showModal" class="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
        <div class="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-xl border border-slate-200 max-h-[90vh] overflow-y-auto">
          <h3 class="text-base font-bold text-slate-900">{{ isEditing ? 'Edit Master Produk' : 'Tambah Master Produk' }}</h3>

          <form @submit.prevent="submitForm" class="space-y-4">
            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Kategori Produk <span class="text-rose-500">*</span></label>
                <select v-model="form.product_category_id" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="" disabled>Pilih Kategori</option>
                  <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
                </select>
              </div>

              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Operator Seluler (Opsional)</label>
                <select v-model="form.mobile_operator_id" class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option :value="null">Bukan Produk Operator</option>
                  <option v-for="op in operators" :key="op.id" :value="op.id">{{ op.name }} ({{ op.code }})</option>
                </select>
              </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Kode Produk <span class="text-rose-500">*</span></label>
                <input v-model="form.code" type="text" placeholder="DANA" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-mono" />
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Brand / Label</label>
                <input v-model="form.brand" type="text" placeholder="DANA" class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1">Nama Produk <span class="text-rose-500">*</span></label>
              <input v-model="form.name" type="text" placeholder="Top Up DANA" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Tipe Transaksi</label>
                <select v-model="form.transaction_type" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
                  <option value="prepaid_direct">Prepaid Direct</option>
                  <option value="prepaid_with_input">Prepaid With Input</option>
                  <option value="prepaid_inquiry">Prepaid Inquiry (Cek Nomor)</option>
                  <option value="postpaid_inquiry">Postpaid Inquiry</option>
                  <option value="voucher_token">Voucher / Token</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Mode Nominal Top Up</label>
                <select v-model="form.amount_mode" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 font-bold">
                  <option value="predefined">Predefined SKU Only</option>
                  <option value="open">Open Amount (Nominal Bebas)</option>
                  <option value="both">Both (SKU & Nominal Bebas)</option>
                </select>
              </div>
            </div>

            <!-- Open Amount Configuration Fields -->
            <div v-if="form.amount_mode === 'open' || form.amount_mode === 'both'" class="p-3 bg-indigo-50/60 rounded-xl border border-indigo-100 space-y-3">
              <p class="text-[11px] font-bold text-indigo-900">Konfigurasi Open Amount (Nominal Bebas)</p>
              <div class="grid grid-cols-3 gap-2">
                <div>
                  <label class="block text-[10px] font-bold text-slate-700 mb-0.5">Min Amount (Rp)</label>
                  <input v-model.number="form.minimum_open_amount_minor" type="number" min="0" placeholder="10000" class="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs bg-white font-mono" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-slate-700 mb-0.5">Max Amount (Opsional)</label>
                  <input v-model.number="form.maximum_open_amount_minor" type="number" min="0" placeholder="Tanpa Batas" class="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs bg-white font-mono" />
                </div>
                <div>
                  <label class="block text-[10px] font-bold text-slate-700 mb-0.5">Kelipatan (Step)</label>
                  <input v-model.number="form.amount_step_minor" type="number" min="1" placeholder="1000" class="w-full px-2.5 py-1.5 rounded-lg border border-slate-300 text-xs bg-white font-mono" />
                </div>
              </div>
            </div>

            <div>
              <label class="block text-xs font-bold text-slate-700 mb-1">Tipe Output</label>
              <select v-model="form.output_type" required class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500">
                <option value="fulfillment">Fulfillment Direct</option>
                <option value="serial_number">Serial Number (SN)</option>
                <option value="token">PLN Token</option>
                <option value="voucher_code">Kode Voucher</option>
                <option value="bill_payment">Struk Tagihan</option>
              </select>
            </div>

            <!-- Input Schema Builder / Raw JSON -->
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-xs font-bold text-slate-700">Skema Input Pelanggan (JSON)</label>
                <button type="button" @click="applyTemplateSchema" class="text-[10px] text-indigo-600 font-bold hover:underline">Gunakan Templat Hp</button>
              </div>
              <textarea v-model="rawSchemaText" rows="4" class="w-full p-3 rounded-xl border border-slate-300 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-slate-50" placeholder='{"fields": [{"key": "phone_number", "label": "Nomor HP", "type": "tel", "required": true}]}'></textarea>
              <p v-if="schemaError" class="text-[11px] text-rose-600 font-semibold mt-1">{{ schemaError }}</p>
            </div>

            <div class="grid grid-cols-2 gap-3">
              <div>
                <label class="block text-xs font-bold text-slate-700 mb-1">Urutan (Sort Order)</label>
                <input v-model.number="form.sort_order" type="number" min="0" class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div class="flex items-center gap-2 pt-6">
                <input v-model="form.is_active" type="checkbox" id="p_is_active" class="rounded text-indigo-600 focus:ring-indigo-500" />
                <label for="p_is_active" class="text-xs font-semibold text-slate-700">Produk Aktif</label>
              </div>
            </div>

            <div class="flex justify-end gap-2 pt-2">
              <button type="button" @click="showModal = false" class="px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 rounded-xl transition">Batal</button>
              <button type="submit" :disabled="form.processing" class="px-4 py-2 text-xs font-bold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition">
                Simpan
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </BackofficeLayout>
</template>

<script setup>
import { ref } from 'vue';
import { useForm, router } from '@inertiajs/vue3';
import BackofficeLayout from '@/Layouts/BackofficeLayout.vue';
import { Plus } from '@lucide/vue';

const props = defineProps({
  products: Array,
  categories: Array,
  operators: Array,
  filters: Object,
});

const search = ref(props.filters?.search || '');
const selectedCategory = ref(props.filters?.product_category_id ? Number(props.filters.product_category_id) : null);
const showModal = ref(false);
const isEditing = ref(false);
const editingProduct = ref(null);
const rawSchemaText = ref('');
const schemaError = ref('');

const form = useForm({
  product_category_id: '',
  mobile_operator_id: null,
  code: '',
  name: '',
  brand: '',
  description: '',
  transaction_type: 'prepaid_direct',
  amount_mode: 'predefined',
  minimum_open_amount_minor: 10000,
  maximum_open_amount_minor: null,
  amount_step_minor: 1000,
  input_schema_json: null,
  output_type: 'fulfillment',
  sort_order: 0,
  is_active: true,
});

const applyTemplateSchema = () => {
  rawSchemaText.value = JSON.stringify({
    fields: [
      {
        key: 'phone_number',
        label: 'Nomor Handphone Terdaftar',
        type: 'tel',
        required: true,
        placeholder: '08xxxxxxxxxx',
        validation: { min_length: 10, max_length: 15, numeric: true }
      }
    ]
  }, null, 2);
  schemaError.value = '';
};

const openCreateModal = () => {
  isEditing.value = false;
  editingProduct.value = null;
  form.reset();
  form.mobile_operator_id = null;
  form.amount_mode = 'predefined';
  form.minimum_open_amount_minor = 10000;
  form.maximum_open_amount_minor = null;
  form.amount_step_minor = 1000;
  applyTemplateSchema();
  form.clearErrors();
  showModal.value = true;
};

const openEditModal = (p) => {
  isEditing.value = true;
  editingProduct.value = p;
  form.product_category_id = p.product_category_id;
  form.mobile_operator_id = p.mobile_operator_id || null;
  form.code = p.code;
  form.name = p.name;
  form.brand = p.brand;
  form.description = p.description;
  form.transaction_type = p.transaction_type;
  form.amount_mode = p.amount_mode || 'predefined';
  form.minimum_open_amount_minor = p.minimum_open_amount_minor ?? 10000;
  form.maximum_open_amount_minor = p.maximum_open_amount_minor ?? null;
  form.amount_step_minor = p.amount_step_minor ?? 1000;
  form.output_type = p.output_type;
  form.sort_order = p.sort_order;
  form.is_active = p.is_active;

  rawSchemaText.value = p.input_schema_json ? JSON.stringify(p.input_schema_json, null, 2) : '';
  schemaError.value = '';
  form.clearErrors();
  showModal.value = true;
};

const submitForm = () => {
  if (rawSchemaText.value.trim()) {
    try {
      form.input_schema_json = JSON.parse(rawSchemaText.value);
      schemaError.value = '';
    } catch (e) {
      schemaError.value = 'Format JSON Schema tidak valid: ' + e.message;
      return;
    }
  } else {
    form.input_schema_json = null;
  }

  if (isEditing.value && editingProduct.value) {
    form.put(route('admin.catalogue.products.update', editingProduct.value.id), {
      onSuccess: () => { showModal.value = false; },
    });
  } else {
    form.post(route('admin.catalogue.products.store'), {
      onSuccess: () => { showModal.value = false; },
    });
  }
};

const toggleActive = (p) => {
  router.patch(route('admin.catalogue.products.toggle-active', p.id), {
    is_active: !p.is_active,
  });
};

const deleteProduct = (p) => {
  if (confirm(`Apakah Anda yakin ingin menghapus master produk '${p.name}'?`)) {
    router.delete(route('admin.catalogue.products.destroy', p.id));
  }
};

const applyFilters = () => {
  router.get(route('admin.catalogue.products.index'), {
    product_category_id: selectedCategory.value,
    search: search.value,
  }, { preserveState: true, replace: true });
};
</script>
